import { BankOutlined, HomeOutlined, RightOutlined } from '@ant-design/icons';
import { Grid, Tag, Typography } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import { orgBrandBlue, orgBrandGreen, orgCardShadow } from './organisationsUi';

type OrgHubEntityRowProps = {
  kind: 'company' | 'farm';
  title: string;
  detail?: string;
  kindLabel?: string;
  onClick?: () => void;
};

const iconByKind: Record<OrgHubEntityRowProps['kind'], ReactNode> = {
  company: <BankOutlined />,
  farm: <HomeOutlined />,
};

export function OrgHubEntityRow({ kind, title, detail, kindLabel, onClick }: OrgHubEntityRowProps) {
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.sm;
  const clickable = typeof onClick === 'function';
  const accent = kind === 'farm' ? orgBrandGreen : orgBrandBlue;

  const rowStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: isCompact ? '14px 16px' : '16px 20px',
    border: 'none',
    borderBottom: '1px solid #f2f4f7',
    background: '#fff',
    textAlign: 'left',
    cursor: clickable ? 'pointer' : 'default',
    transition: 'background 0.15s ease',
    font: 'inherit',
  };

  const content = (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, flex: 1 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: kind === 'farm' ? '#ecfdf3' : '#eff8ff',
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: 16,
          }}
        >
          {iconByKind[kind]}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Typography.Text strong style={{ fontSize: 16, color: '#101828', wordBreak: 'break-word' }}>
              {title}
            </Typography.Text>
            {kindLabel ? (
              <Tag
                style={{
                  margin: 0,
                  borderRadius: 999,
                  borderColor: 'transparent',
                  background: kind === 'farm' ? '#ecfdf3' : '#eff8ff',
                  color: accent,
                  fontWeight: 600,
                }}
              >
                {kindLabel}
              </Tag>
            ) : null}
          </div>
          {detail ? (
            <Typography.Text style={{ display: 'block', marginTop: 4, fontSize: 14, color: '#667085' }}>
              {detail}
            </Typography.Text>
          ) : null}
        </div>
      </div>
      {clickable ? <RightOutlined style={{ color: '#98a2b3', flexShrink: 0 }} /> : null}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={rowStyle}
        onMouseEnter={(event) => {
          event.currentTarget.style.background = '#f8fafc';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.background = '#fff';
        }}
      >
        {content}
      </button>
    );
  }

  return <div style={rowStyle}>{content}</div>;
}

type OrgHubEntityListProps = {
  children: ReactNode;
  empty?: boolean;
  emptyLabel: string;
  footer?: ReactNode;
};

export function OrgHubEntityList({ children, empty, emptyLabel, footer }: OrgHubEntityListProps) {
  if (empty) {
    return (
      <section
        style={{
          background: '#fff',
          borderRadius: 8,
          boxShadow: orgCardShadow,
          padding: '28px 20px',
          textAlign: 'center',
          marginBottom: 20,
        }}
      >
        <Typography.Text style={{ color: '#667085' }}>{emptyLabel}</Typography.Text>
      </section>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: orgCardShadow,
        overflow: 'hidden',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
      {footer ? (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f2f4f7', display: 'flex', justifyContent: 'flex-end' }}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}
